import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {shallowEqual, useSelector} from 'react-redux';
import Swiper from 'react-id-swiper';
import {NavLink} from '@nl/core';
import {Nav} from '@nl/component';
import {useI18n} from '@nl/i18n';
import MVPDBrand from '../components/myaccount/MVPDBrand';
import getRouterConfig from '../utils/getRouterConfig';
import {event, EVENT_TYPES} from '../utils/event';
import {getURLParamByName} from '../utils/utils';

import 'swiper/swiper.scss';
import '../styles/header/header.scss';

const NO_HEADER_PAGE_PATHS = ['/streaming-subscriptions', '/packages'];
function getMenusConfig(isAccountPage, isNBAAccountPage, isExternalPage, locServer,
												watchSitePrefix) {
	const homeMenusConfig = [
		{
			path: `${isExternalPage ? watchSitePrefix : '/'}featured`,
			pathMatch: ['/featured'],
		},
		{
			path: `${isExternalPage ? watchSitePrefix : '/'}nba-tv`,
			pathMatch: ['/nba-tv', '/channel/nbatvlive', '/nba-tv/series', '/page/show'],
		},
		{
			path: `${isExternalPage ? watchSitePrefix : '/'}league-pass`,
			pathMatch: ['/league-pass', '/event/'],
		},
	];

	const accountMenusConfig = [
		{
			path: `${locServer}account/profile`,
			pathMatch: ['/account/profile'],
		},
		{
			path: `${locServer}account/favorites#team`,
			pathMatch: ['#team'],
			className: 'fav-team',
		},
		{
			path: `${locServer}account/favorites#player`,
			pathMatch: ['#player'],
			className: 'fav-player',
		},
		{
			path: `${locServer}account/history`,
			pathMatch: ['/account/history'],
		},
	];

	const nbaAccountMenusConfig = [
		{
			path: `${locServer}account/nbaprofile`,
			pathMatch: ['/account/nbaprofile'],
		},
		{
			path: `${locServer}account/nbafavorites#team`,
			pathMatch: ['#team'],
			className: 'fav-team',
		},
		{
			path: `${locServer}account/nbafavorites#player`,
			pathMatch: ['#player'],
			className: 'fav-player',
		},
	];
	let menuConfig = homeMenusConfig;
	if (isAccountPage) {
		menuConfig = isNBAAccountPage ? nbaAccountMenusConfig : accountMenusConfig;
	}
	return menuConfig;
}
const getHubInfo = (results) => {
	const hubInfo = {};
	if (results && results.categoryPrimary) {
		const {bannerImage, isHub, menu} = results.categoryPrimary;
		if (isHub) {
			hubInfo.isHub = isHub;
			const hubMenusName = [];
			const hubMenusConfig = [];
			if (bannerImage) {
				hubInfo.bannerImage = bannerImage;
			}
			if (menu) {
				const {name, links} = menu;
				hubInfo.name = name;
				if (links && links.length > 0) {
					for (let i = 0; i < links.length; i += 1) {
						hubMenusName.push(links[i].text);
						hubMenusConfig.push({path: links[i].href});
					}
				}
			}
			if (hubMenusName.length > 0) {
				hubInfo.menusItem = hubMenusName;
			}
			if (hubMenusConfig.length > 0) {
				hubInfo.menus = hubMenusConfig;
			}
		}
	}
	return hubInfo;
};

function Header() {
	const {pathname, hash, search} = useLocation();
	const {
		headerSwiperConfig,
		LOC_SERVER,
		WATCH_SITE_PREFIX,
	} = useSelector((state) => ({
		headerSwiperConfig: state.config.data.OWL_OPTIONS.headerOwl,
		LOC_SERVER: state.config.data.LOC_SERVER,
		WATCH_SITE_PREFIX: state.config.data.WATCH_SITE_PREFIX,
	}), shallowEqual);
	const isMyAccountPage = pathname === '/account/profile' || pathname === '/account/favorites' || pathname === '/account/history' || pathname === '/account/nbaprofile' || pathname === '/account/nbafavorites';
	const isNBAMyAccountPage = pathname === '/account/nbaprofile' || pathname === '/account/nbafavorites';
	const isExternalPage = getRouterConfig(pathname, 'externalPage');
	const [bannerImage, setBannerImage] = useState('');
	const menusNameKey = isMyAccountPage
			? ['account_manage_account', 'account_favorite_teams', 'account_favorite_players', 'account_viewing_history']
			: ['featured', 'nba_tv', 'league_pass'];
	const menuSiteKey = isMyAccountPage ? 'header_menu_account_my_account' : 'header_menu_watch';
	const menusName = useI18n(menusNameKey, {keyPrefix: 'header_menu_'});
	const [menuSite] = useI18n(menuSiteKey);
	const menusConfig = getMenusConfig(isMyAccountPage, isNBAMyAccountPage, isExternalPage,
			LOC_SERVER, WATCH_SITE_PREFIX);
	const [selectMenuIndex, setSelectMenuIndex] = useState(-1);
	const [menusItem, setMenusItem] = useState(menusName);
	const [menuTitle, setMenuTitle] = useState(menuSite);
	const [menus, setMenus] = useState(menusConfig);
	const [isHubPage, setIsHubPage] = useState(false);
	const [hideNav, setHideNav] = useState(false);

	const menusList = useMemo(() => (menus.map((menu, index) => (
			<Nav.Item key={`${menu.path}`} className={`${menu.className || ''}`}>
				<NavLink key={`${menu.path}`}
								 className={index === selectMenuIndex ? 'is-selected' : ''}
								 to={menu.path}>
					{menusItem[index]}
				</NavLink>
			</Nav.Item>
	))), [menus, menusItem, selectMenuIndex]);
	const getActiveMenuIndex = useCallback((menusArr, path) => {
		if (path !== '/') {
			for (let i = 0; i < menusArr.length; i += 1) {
				const menu = menusArr[i];
				if (menu.pathMatch) {
					for (const match of menu.pathMatch) {
						if (hash && hash === match) {
							return i;
						}
						if (path.indexOf(match) === 0) {
							return i;
						}
					}
				}
			}
			return -1;
		}
		return 0;
	}, [hash]);

	const resetHeader = useCallback(() => {
		setBannerImage('');
		setMenusItem(menusName);
		setMenuTitle(menuSite);
		setMenus(menusConfig);
	}, [menusName, menuSite, menusConfig]);

	useEffect(() => {
		let isMounted = true;
		const dataCallback = (info) => {
			const data = getHubInfo(info);
			if (isMounted) {
				if (data && data.isHub) {
					if (data.bannerImage) {
						setBannerImage(data.bannerImage);
					}
					if (data.name) {
						setMenuTitle(data.name);
					}
					if (data.menusItem) {
						setMenusItem(data.menusItem);
					}
					if (data.menus) {
						setMenus(data.menus);
					}
					setIsHubPage(true);
				} else if (isHubPage) {
					setIsHubPage(false);
					resetHeader();
				}
			}
		};
		event.on(EVENT_TYPES.VIDEO_DETAIL_NBA_FEED_READY, dataCallback);
		return () => {
			isMounted = false;
			event.off(EVENT_TYPES.VIDEO_DETAIL_NBA_FEED_READY, dataCallback);
		};
	}, [resetHeader, isHubPage]);

	useEffect(() => {
		const activeMenuIndex = getActiveMenuIndex(menus, pathname);
		setSelectMenuIndex(activeMenuIndex);
		// eslint-disable-next-line
	}, [menus, pathname, hash]);
	useEffect(() => {
		setHideNav(getURLParamByName('hidenav', search) === 'true');
	}, [search]);

	const styleComponentBanner = {
		backgroundImage: `url(${bannerImage})`,
	};
	if ((NO_HEADER_PAGE_PATHS.indexOf(pathname) !== -1) || (!isHubPage && hideNav)) {
		return <></>;
	}

	return (
			<div>
				<div className="header">
					<div className="nav-bar">
						<div className="nav-bar__text">{menuTitle}</div>
						<div className="nav-bar__line" />
						<Nav fill id="subMenus">
							<Swiper {...headerSwiperConfig}>
								{menusList}
							</Swiper>
						</Nav>
						<MVPDBrand />
					</div>
				</div>
				<div className="nav-header-placeholder" />
				{bannerImage && <div className="banner-image" style={styleComponentBanner} />}
			</div>
	);
}

export default Header;
